import { GAME_ECONOMY_SETTINGS } from 'game/GameConstants';
import { MIN_PIGGY_BANK_LEVEL, PIGGY_BANK_FAIL_STREAK_LIMIT, PIGGY_BANK_TIER_CONFIG_MAP, PIGGY_BANK_TIER_PROGRESSION } from './PiggyBankConstants';
import type { PiggyBankObservable } from './types';
import { PiggyBankTier } from './types';
import type { PiggyBankState } from './PiggyBankState';
import { MS_IN_HOUR } from '../../utils/time-utils';

const defaultPiggyBankState: PiggyBankState = {
  coins: 0,
  maxCoins: GAME_ECONOMY_SETTINGS.piggyBankCoinCapacity,
  fullPiggyBankShown: false,
  currentTier: PiggyBankTier.Legacy,
  endTime: null,
  failStreak: 0,
};

export function getDefaultState(): PiggyBankState {
  return {
    ...defaultPiggyBankState,
  };
}

export function arePiggyBankTiersActive(): boolean {
  return true;
}

export function addCoins(state: PiggyBankState, coinsToAdd: number): PiggyBankState {
  state.coins = Math.min(state.coins + coinsToAdd, state.maxCoins);
  return state;
}

export function getPiggyBankDefaultStateByTier(tier: PiggyBankTier): PiggyBankState {
  const tierConfig = PIGGY_BANK_TIER_CONFIG_MAP[tier];

  return {
    currentTier: tier,
    coins: 0,
    maxCoins: tierConfig.maxCoins,
    endTime: tierConfig.durationHours ? Date.now() + tierConfig.durationHours * MS_IN_HOUR : null,
    failStreak: 0,
    fullPiggyBankShown: false,
  };
}

export function getPiggyBankState(observable: PiggyBankObservable): PiggyBankState {
  const { piggyBank } = observable.getValue();
  return piggyBank ?? getDefaultState();
}

export function isPiggyBankAvailable(observable: PiggyBankObservable): boolean {
  const { levelIndex } = observable.getValue();
  return levelIndex + 1 >= MIN_PIGGY_BANK_LEVEL;
}

export function isPiggyBankFull(observable: PiggyBankObservable): boolean {
  const piggyBank = getPiggyBankState(observable);
  return piggyBank.coins >= piggyBank.maxCoins;
}

export function getNextPiggyBankTier(currentTier: PiggyBankTier): PiggyBankTier {
  if (currentTier === PiggyBankTier.Legacy) return currentTier;

  const index = PIGGY_BANK_TIER_PROGRESSION.indexOf(currentTier);
  if (index === -1) return PiggyBankTier.Normal;
  if (index >= PIGGY_BANK_TIER_PROGRESSION.length - 1) return currentTier;

  return PIGGY_BANK_TIER_PROGRESSION[index + 1];
}

export function getPreviousPiggyBankTier(currentTier: PiggyBankTier): PiggyBankTier {
  if (currentTier === PiggyBankTier.Legacy) return currentTier;

  const index = PIGGY_BANK_TIER_PROGRESSION.indexOf(currentTier);
  if (index <= 0) return PiggyBankTier.Normal;

  return PIGGY_BANK_TIER_PROGRESSION[index - 1];
}

export function increasePiggyBankTier(observable: PiggyBankObservable): boolean {
  const piggyBank = getPiggyBankState(observable);

  const nextTier = getNextPiggyBankTier(piggyBank.currentTier);
  const hasIncreasedTier = piggyBank.currentTier !== nextTier;
  const tierConfig = PIGGY_BANK_TIER_CONFIG_MAP[nextTier];

  observable.updateValue({
    piggyBank: {
      currentTier: nextTier,
      coins: 0,
      maxCoins: tierConfig.maxCoins,
      failStreak: 0,
      endTime: tierConfig.durationHours ? Date.now() + tierConfig.durationHours * MS_IN_HOUR : null,
      fullPiggyBankShown: false,
    },
  });

  return hasIncreasedTier;
}

export function decreasePiggyBankTierIfNeeded(observable: PiggyBankObservable, forceDecrease = false): void {
  const piggyBank = getPiggyBankState(observable);

  let { currentTier, failStreak } = piggyBank;
  if (!arePiggyBankTiersActive()) {
    // Offboarding case when tiers are deactivated through split test
    currentTier = PiggyBankTier.Legacy;
    failStreak = 0;
  } else if (failStreak >= PIGGY_BANK_FAIL_STREAK_LIMIT || forceDecrease) {
    currentTier = getPreviousPiggyBankTier(currentTier);
    failStreak = 0;
  } else {
    failStreak += 1;
  }

  const tierConfig = PIGGY_BANK_TIER_CONFIG_MAP[currentTier];

  observable.updateValue({
    piggyBank: {
      currentTier,
      coins: 0,
      maxCoins: tierConfig.maxCoins,
      failStreak,
      endTime: tierConfig.durationHours ? Date.now() + tierConfig.durationHours * MS_IN_HOUR : null,
      fullPiggyBankShown: false,
    },
  });
}

export function isPiggyBankExpired(observable: PiggyBankObservable): boolean {
  const piggyBank = getPiggyBankState(observable);
  if (!piggyBank.endTime) return false;

  return Date.now() >= piggyBank.endTime;
}

export function onboardPiggyBankTier(observable: PiggyBankObservable): void {
  const piggyBank = getPiggyBankState(observable);

  if (piggyBank.currentTier === PiggyBankTier.Legacy && arePiggyBankTiersActive()) {
    const firstTier = PIGGY_BANK_TIER_PROGRESSION[0];

    const tierConfig = PIGGY_BANK_TIER_CONFIG_MAP[firstTier];

    observable.updateValue({
      piggyBank: {
        ...piggyBank,
        currentTier: firstTier,
        endTime: tierConfig.durationHours ? Date.now() + tierConfig.durationHours * MS_IN_HOUR : null,
      },
    });
  }
}